import { Box } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { StringHelper } from 'classes/helpers/string.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonProgress } from 'components/common/progress';
import { SessionEventsContext } from 'contexts/session-events.context';
import { addDays, startOfToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { LOCAL_DATETIME_FORMAT_SHORT, LOCAL_TIMEZONE } from 'enums/env';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICombinedData } from 'lib_ts/interfaces/csv/exports/i-session-fires';
import { useContext, useMemo, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import slugify from 'slugify';

const COMPONENT_NAME = 'ExportSessionsDialog';

interface IProps {
  machineIDs: string[];
  onClose: () => void;
}

export const ExportSessionsDialog = (props: IProps) => {
  const { getShotsData } = useContext(SessionEventsContext);

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(addDays(startOfToday(), -3));
  const [endDate, setEndDate] = useState(startOfToday());

  // # of sessions found
  const [total, setTotal] = useState(1);

  // # of sessions processed
  const [touched, setTouched] = useState(0);

  const progress = useMemo(
    () => (total > 0 ? 100 * (touched / total) : 0),
    [total, touched]
  );

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.MD}
        title="Export Machine Sessions"
        content={
          <CommonFormGrid columns={2}>
            <Box gridColumn="span 2">
              Export sessions for {props.machineIDs.join(', ')}.
            </Box>

            <CommonDateInput
              id="export-sessions-start"
              label="common.start-date"
              disabled={loading}
              defaultValue={startDate}
              onChange={(date) => {
                if (!date) {
                  NotifyHelper.warning({
                    message_md: t('common.check-inputs-msg'),
                  });
                  return;
                }

                setStartDate(date);
              }}
              showTime
            />
            <CommonDateInput
              id="export-sessions-end"
              label="common.end-date"
              disabled={loading}
              defaultValue={endDate}
              onChange={(date) => {
                if (!date) {
                  NotifyHelper.warning({
                    message_md: t('common.check-inputs-msg'),
                  });
                  return;
                }

                setEndDate(date);
              }}
              showTime
            />

            {loading && (
              <Box gridColumn="span 2">
                <CommonProgress
                  value={progress}
                  label={`Session ${touched} of ${total} processed`}
                />
              </Box>
            )}
          </CommonFormGrid>
        }
        loading={loading}
        buttons={[
          {
            label: 'common.export',
            color: RADIX.COLOR.INFO,
            onClick: async () => {
              NotifyHelper.success({
                message_md: `Please wait while your request is processed, this may take a while depending on the filter parameters.`,
                delay_ms: 0,
              });

              if (loading) {
                // already processing something
                return;
              }

              setLoading(true);

              // get all sessions satisfying the filter
              const sessions =
                await SessionEventsService.summarizeMachinesSessions({
                  machineIDs: props.machineIDs,
                  start_date: startDate,
                  end_date: endDate,
                  exclude_no_fires: true,
                  limit: 50,
                });

              if (sessions.length === 0) {
                setTotal(1);
                setTouched(0);
                setLoading(false);
                return;
              }

              // reset counters
              setTotal(sessions.length);
              setTouched(0);

              const sessionsByMachine = ArrayHelper.groupBy(
                sessions,
                'machineID'
              );

              for (const machineSessions of Object.values(sessionsByMachine)) {
                const machineRows: ICombinedData[] = [];

                const sampleSession = machineSessions.find((m) => m.machineID);

                if (!sampleSession) {
                  // shouldn't trigger but we don't want to proceed if we have no machineID
                  setTouched((prev) => prev + machineSessions.length);
                  continue;
                }

                for (const s of machineSessions) {
                  setTouched((prev) => prev + 1);

                  const { sessionID } = s;

                  const sessionRows = await getShotsData({
                    machineID: sampleSession.machineID,
                    session: sessionID,
                    silently: true,
                  });

                  if (sessionRows.length === 0) {
                    continue;
                  }

                  machineRows.push(...sessionRows);
                }

                if (machineRows.length === 0) {
                  if (sampleSession) {
                    NotifyHelper.warning({
                      message_md: `No data to export for \`${sampleSession?.machineID}\`.`,
                    });
                  }
                  continue;
                }

                const filenamePrefix = slugify(
                  [
                    sampleSession.machineID,
                    sampleSession.nickname ?? '',
                    format(startDate, LOCAL_DATETIME_FORMAT_SHORT, {
                      timeZone: LOCAL_TIMEZONE,
                    }),
                    format(endDate, LOCAL_DATETIME_FORMAT_SHORT, {
                      timeZone: LOCAL_TIMEZONE,
                    }),
                  ]
                    .map((m) => m?.trim())
                    .filter((m) => m)
                    .join('-'),
                  { strict: true }
                );

                await StringHelper.saveCsv(machineRows, {
                  maxRows: 200,
                  prefix: filenamePrefix,
                });
              }

              setLoading(false);
              props.onClose();
            },
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
